import { blue, green, grey } from '@mui/material/colors';
import { find } from 'lodash';
import React, { ReactElement } from 'react';
import { Icons } from '../icons/Icons';
import { APPLICATION_NAMES } from './constants';

/**
 * Represents a top-level application in the Showrunnr UI navigation.
 */
export type AppDefinition = {
    /** Unique identifier for the application */
    id: string;
    /* Display name of the application which should 
    be in sync with backend application name.
    Can be considered as Branded Application */
    name: string;
    /** Route path for the application's home page */
    home: string;
    /**
     * Set of feature app IDs related to this branded application.
     * Includes the branded app and associated apps that share permissions.
     * Used by the permission system to check feature access across related apps.
     */
    featureApps: string[];
    /** Optional React element for the application's icon */
    icon?: ReactElement;
};

export const PROFILES: AppDefinition = {
    id: 'profiles',
    name: APPLICATION_NAMES.profiles,
    home: '/profiles',
    featureApps: [APPLICATION_NAMES.profiles, APPLICATION_NAMES.globalLanguages]
};

export const ADMINISTRATION: AppDefinition = {
    id: 'admin',
    name: APPLICATION_NAMES.administration,
    home: '/admin',
    featureApps: [
        APPLICATION_NAMES.administration,
        APPLICATION_NAMES.mcpServer,
        APPLICATION_NAMES.globalLanguages,
        APPLICATION_NAMES.dealEssentials
    ],
    icon: <Icons.Account sx={{ color: grey.A400 }} />
};

export const SUPPLIER: AppDefinition = {
    id: 'supplier',
    name: APPLICATION_NAMES.reservationManagement,
    home: '/supplier',
    featureApps: [APPLICATION_NAMES.reservationManagement, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.EventAvailable sx={{ color: green.A400 }} />
};

export const MARKETPLACE: AppDefinition = {
    id: 'marketplace',
    name: APPLICATION_NAMES.marketplace,
    home: '/marketplace',
    featureApps: [APPLICATION_NAMES.marketplace, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.Store sx={{ color: blue.A400 }} />
};

export const PLANNING: AppDefinition = {
    id: 'planning',
    name: APPLICATION_NAMES.planning,
    featureApps: [
        APPLICATION_NAMES.planning,
        APPLICATION_NAMES.procurement,
        APPLICATION_NAMES.globalLanguages,
        APPLICATION_NAMES.dealEssentials,
        APPLICATION_NAMES.dealOperations,
        APPLICATION_NAMES.dealFinancials
    ],
    home: '/planning',
    icon: <Icons.Assignment sx={{ color: blue.A400 }} />
};

export const GUEST: AppDefinition = {
    id: 'guest',
    name: APPLICATION_NAMES.guest,
    home: '/guest',
    featureApps: [APPLICATION_NAMES.guest, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.Assignment sx={{ color: blue.A400 }} />
};

export const RESERVATION_PROFESSIONAL: AppDefinition = {
    id: 'reservationProfessional',
    name: APPLICATION_NAMES.reservationProfessional,
    home: '/reservationProfessional',
    featureApps: [APPLICATION_NAMES.reservationProfessional, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.EventAvailable sx={{ color: blue.A400 }} />
};

export const PRODUCTION_PROFESSIONAL: AppDefinition = {
    id: 'productionProfessional',
    name: APPLICATION_NAMES.productionProfessional,
    home: '/productionProfessional',
    featureApps: [APPLICATION_NAMES.productionProfessional, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.Assignment sx={{ color: blue.A400 }} />
};

export const LIGHTING_PROFESSIONAL: AppDefinition = {
    id: 'lightingProfessional',
    name: APPLICATION_NAMES.lightingProfessional,
    home: '/lightingProfessional',
    featureApps: [APPLICATION_NAMES.lightingProfessional, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.Assignment sx={{ color: blue.A400 }} />
};

export const PRODUCTION_CONNECT: AppDefinition = {
    id: 'productionConnect',
    name: APPLICATION_NAMES.productionConnect,
    home: '/productionConnect',
    featureApps: [APPLICATION_NAMES.productionConnect, APPLICATION_NAMES.globalLanguages],
    icon: <Icons.Assignment sx={{ color: blue.A400 }} />
};

export const APPLICATIONS: AppDefinition[] = [
    PROFILES,
    ADMINISTRATION,
    SUPPLIER,
    MARKETPLACE,
    PLANNING,
    GUEST,
    RESERVATION_PROFESSIONAL,
    PRODUCTION_PROFESSIONAL,
    LIGHTING_PROFESSIONAL,
    PRODUCTION_CONNECT
];

export const getApplicationById = (id: string): AppDefinition => {
    return find(APPLICATIONS, (app) => app.id === id);
};

export const getApplicationByName = (name: string): AppDefinition => {
    return find(APPLICATIONS, (app) => app.name === name);
};
